import { useEffect, useMemo, useRef } from 'react';
import { ECGRenderer } from 'ecg-viewer-core';
import { IconButton, Tooltip } from '@mui/material';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';

const EcgCanvasChart = ({
    overviewSamples,
    overviewSamplingRate,
    viewportSamples,
    viewportSamplingRate,
    viewportSignalStartSec = 0,
    viewportStartSec = 0,
    viewportDurationSec = 10,
    onViewportChange
}) => {
    const containerRef = useRef(null);
    const mainCanvasRef = useRef(null);
    const minimapCanvasRef = useRef(null);
    const rendererRef = useRef(null);

    const overviewFloat = useMemo(() => {
        if (!overviewSamples?.length) return null;
        return Float32Array.from(overviewSamples);
    }, [overviewSamples]);

    const viewportFloat = useMemo(() => {
        if (!viewportSamples?.length) return null;
        return Float32Array.from(viewportSamples);
    }, [viewportSamples]);

    useEffect(() => {
        if (!mainCanvasRef.current || !minimapCanvasRef.current) return;

        rendererRef.current = new ECGRenderer(
            mainCanvasRef.current,
            minimapCanvasRef.current
        );

        const unsubscribe = rendererRef.current.on('viewport:changed', ({ viewport }) => {
            if (onViewportChange) onViewportChange(viewport);
        });

        return () => {
            if (unsubscribe) unsubscribe();
            rendererRef.current = null;
        };
    }, [onViewportChange]);

    useEffect(() => {
        if (!containerRef.current || !rendererRef.current) return;

        const resize = () => {
            const width = containerRef.current.offsetWidth;

            if (mainCanvasRef.current && minimapCanvasRef.current) {
                mainCanvasRef.current.width = width;
                mainCanvasRef.current.height = 600;
                minimapCanvasRef.current.width = width;
                minimapCanvasRef.current.height = 100;
            }

            rendererRef.current?.resize();
        };

        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!rendererRef.current || !overviewFloat?.length || !overviewSamplingRate) return;

        rendererRef.current.setOverviewSignal({
            signalId: 'lead-I',
            leadName: 'Lead I',
            samples: overviewFloat,
            sampleRateHz: overviewSamplingRate,
            unit: 'uV',
            startTimeSec: 0,
            durationSec: overviewFloat.length / overviewSamplingRate
        });
    }, [overviewFloat, overviewSamplingRate]);

    useEffect(() => {
        if (!rendererRef.current || !viewportFloat?.length || !viewportSamplingRate) return;

        rendererRef.current.setViewportSignal({
            signalId: 'lead-I',
            leadName: 'Lead I',
            samples: viewportFloat,
            sampleRateHz: viewportSamplingRate,
            unit: 'uV',
            startSec: viewportSignalStartSec,
            startTimeSec: viewportSignalStartSec,
            durationSec: viewportFloat.length / viewportSamplingRate
        });
    }, [viewportFloat, viewportSamplingRate, viewportSignalStartSec]);

    useEffect(() => {
        if (!rendererRef.current) return;
        rendererRef.current.setViewport(viewportStartSec, viewportDurationSec);
    }, [viewportStartSec, viewportDurationSec]);

    const handleResetZoom = () => {
        rendererRef.current?.resetZoom();
    };

    return (
        <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
            <Tooltip title="Reset zoom">
                <IconButton
                    onClick={handleResetZoom}
                    size="small"
                    sx={{
                        position: 'absolute',
                        bottom: 158,
                        right: 16,
                        zIndex: 10,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,1)'
                        }
                    }}
                >
                    <ZoomOutMapIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            <canvas
                ref={mainCanvasRef}
                style={{ width: '100%', height: 600, display: 'block' }}
            />

            <canvas
                ref={minimapCanvasRef}
                style={{ width: '100%', height: 100, display: 'block', marginTop: 8 }}
            />
        </div>
    );
};

export default EcgCanvasChart;
